import React, { useEffect, useRef } from 'react';
import { CheckCircle, AlertCircle, Loader, Clock } from 'lucide-react';

const StatusPanel = ({ logs, isProcessing, isComplete, hasError, compact }) => {
  const logContainerRef = useRef(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);
  const getLogIcon = (source) => {
    if (source === 'error') {
      return <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />;
    }
    if (source === 'backend') {
      return <CheckCircle className="h-3 w-3 text-blue-500 flex-shrink-0" />;
    }
    return <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />;
  };

  const getLogColor = (source) => {
    if (source === 'error') return 'text-red-600';
    if (source === 'backend') return 'text-blue-600';
    return 'text-gray-700';
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col"
      style={compact ? { minHeight: '200px', maxHeight: '360px', height: '100%' } : { minHeight: '500px', maxHeight: '500px' }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Processing Status</h3>
        <div className="flex items-center space-x-2">
          {isProcessing && (
            <div className="flex items-center space-x-1 text-blue-600">
              <Loader className="h-4 w-4 animate-spin" />
              <span className="text-xs">Processing...</span>
            </div>
          )}
          {isComplete && !isProcessing && !hasError && (
            <div className="flex items-center space-x-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span className="text-xs">Complete</span>
            </div>
          )}
          {hasError && (
            <div className="flex items-center space-x-1 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span className="text-xs">Error</span>
            </div>
          )}
        </div>
      </div>

      <div ref={logContainerRef} className="flex-1 overflow-y-auto space-y-2">
        {logs.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No activity yet...</p>
        ) : (
          logs.map((log, index) => {
            // Check if this is a separator
            if (log.message === '---SEPARATOR---') {
              return (
                <div key={index} className="border-t border-gray-300 my-2 pt-2">
                  <p className="text-xs text-gray-500 italic">New Request</p>
                </div>
              );
            }

            return (
              <div
                key={index}
                className={`flex items-start space-x-2 text-xs ${getLogColor(log.source)}`}
              >
                {getLogIcon(log.source)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500 font-mono text-[10px]">
                      {log.timestamp}
                    </span>
                  </div>
                  <p className="mt-0.5 break-words">{log.message}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StatusPanel;

